import { ReactNode } from 'react';
import { AppView, CART_WARNING_MODAL_TEXTS } from '../constants';
import { formatCurrency } from '../formattingUtils';
import { useGetCart } from '../services/cartManager';
import { useConfirm } from '../services/confirmationModalManager';
import {
  ConfigurationResponse,
  useGetCheckoutConfiguration,
} from '../services/lendersConfigurationManager';
import { ModalHeaderWithExitButton } from './ModalHeaderWithExitButton';
import { Box, styled } from '@mui/material';
import { Typography, VerticalSpace } from '@chargeafter/ui';
import { confirmModalStyles } from './confirmModalStyles';
import { getFlags } from '@chargeafter/feature-flag';

const StyledContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  color: theme.palette.grey[800],
  minWidth: theme.spacing(100),
}));

const prepareModalContent = (
  lendersConfigurations = {} as ConfigurationResponse,
  totalAmount: number,
): {
  messages: ReactNode;
  variant: 'minAmount' | 'maxAmount';
} | null => {
  const { lendersAboveMaxAmount, lendersBelowMinAmount } =
    lendersConfigurations.lendersConfigurations.reduce(
      (acc, curr) => {
        if (curr.minimalCartValue && curr.minimalCartValue > totalAmount) {
          acc?.lendersBelowMinAmount?.push({
            lenderName: curr.name,
            amount: curr.minimalCartValue,
          });
        }

        if (curr.maximalCartValue && curr.maximalCartValue < totalAmount) {
          acc?.lendersAboveMaxAmount?.push({
            lenderName: curr.name,
            amount: curr.maximalCartValue,
          });
        }

        return acc;
      },
      {
        lendersBelowMinAmount: [],
        lendersAboveMaxAmount: [],
      } as {
        lendersBelowMinAmount: {
          lenderName: string;
          amount: number;
        }[];
        lendersAboveMaxAmount: {
          lenderName: string;
          amount: number;
        }[];
      },
    );

  if (lendersAboveMaxAmount?.length) {
    return {
      variant: 'maxAmount',
      messages: lendersAboveMaxAmount.map(({ lenderName, amount }) => (
        <Typography variant="body1" lineHeight={'30px'}>
          To checkout with {lenderName}, the cart cannot exceed of{' '}
          <b>{formatCurrency(amount)}</b>
        </Typography>
      )),
    };
  }

  if (lendersBelowMinAmount?.length) {
    return {
      variant: 'minAmount',
      messages: lendersBelowMinAmount.map(({ lenderName, amount }) => (
        <Typography variant="body1" lineHeight={'30px'}>
          To checkout with {lenderName}, the cart must have a minimum of{' '}
          <b>{formatCurrency(amount)}</b>
        </Typography>
      )),
    };
  }

  return null;
};

export const useCartLimitationModal = () => {
  const deviceSelection = useConfirm(AppView.CartWarningModal);
  const { totalAmount } = useGetCart();
  const checkoutConfiguration = useGetCheckoutConfiguration();
  const { ffSalesAppShouldUseCartLimitation } = getFlags();

  return async () => {
    if (
      !ffSalesAppShouldUseCartLimitation ||
      checkoutConfiguration?.lendersConfigurations.length === 1
    ) {
      return true;
    }

    const modalContent = prepareModalContent(
      checkoutConfiguration,
      totalAmount,
    );

    if (!modalContent) {
      return true;
    }

    try {
      await deviceSelection({
        ...confirmModalStyles,
        allowClose: true,
        dialogProps: {
          PaperProps: {
            'data-testid': 'cart-limitation-modal',
          },
        },
        confirmationText: CART_WARNING_MODAL_TEXTS.CONFIRM_BUTTON,
        cancellationText: CART_WARNING_MODAL_TEXTS.CANCEL_BUTTON,
        content: (
          <StyledContainer>
            <Typography variant="body3U" fontWeight="bold">
              {CART_WARNING_MODAL_TEXTS.SECONDARY_TITLE}
            </Typography>
            <VerticalSpace units={1} />
            {modalContent.messages}
            <Typography variant="body1" lineHeight={'30px'}>
              {CART_WARNING_MODAL_TEXTS.INSTRUCTION}
            </Typography>
          </StyledContainer>
        ),
        title: (
          <ModalHeaderWithExitButton
            title={
              modalContent.variant === 'maxAmount'
                ? CART_WARNING_MODAL_TEXTS.MAX_AMOUNT_TITLE
                : CART_WARNING_MODAL_TEXTS.MIN_AMOUNT_TITLE
            }
          />
        ),
      });
      return true;
    } catch {
      return false;
    }
  };
};
